import { FC, useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { format } from 'date-fns';
import InfiniteScroll from 'react-infinite-scroll-component';

import { MessageRow } from '@libs/models';
import { useChat } from '@carcareers/contexts';
import { useAuth } from '@carcareers/hooks';
import { MessageLoader } from '@carcareers/components';

export const MessageList: FC = () => {
  const { session } = useAuth();
  const { messages, loadingMessages, loadMessages, hasMoreMessages } = useChat();
  const currentUserId = session?.user?.id;
  const messagesLength = useMemo(() => Object.keys(messages).length, [messages]);

  if (loadingMessages && messagesLength === 0) {
    return <MessageLoader />;
  }

  if (messagesLength === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          backgroundColor: 'grey.100',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No messages yet. Start the conversation!
        </Typography>
      </Box>
    );
  }

  const messagesList = Object.values(messages).sort((a, b) => 
    new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
  );

  const groupedMessages: Record<string, MessageRow[]> = {};
  messagesList.forEach((message) => {
    const date = new Date(message.sent_at);
    const dateKey = format(date, 'yyyy-MM-dd');
    if (!groupedMessages[dateKey]) {
      groupedMessages[dateKey] = [];
    }
    groupedMessages[dateKey].push(message);
  });

  const sortedDateKeys = Object.keys(groupedMessages).sort((a, b) => 
    new Date(a).getTime() - new Date(b).getTime()
  );

  return (
    <Box
      id="messageListScrollable"
      sx={{
        display: 'flex',
        flexDirection: 'column-reverse',
        p: 2,
        gap: 2,
        overflow: 'auto',
        backgroundColor: 'grey.100',
        width: '100%',
        overflowAnchor: 'none',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}
    >
      <InfiniteScroll
        scrollableTarget="messageListScrollable"
        dataLength={messagesLength}
        next={loadMessages}
        hasMore={hasMoreMessages}
        loader={<MessageLoader />}
        inverse={true}
        style={{ display: 'flex', flexDirection: 'column-reverse' }}
        endMessage={
          messagesLength === 0 ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography>No messages yet.</Typography>
            </Box>
          ) : null
        }
      >
        {sortedDateKeys.reverse().map((dateKey) => (
          <Box key={dateKey} sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  bgcolor: 'background.paper',
                  px: 2,
                  py: 0.5,
                  borderRadius: 10,
                  color: 'text.secondary',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                {format(new Date(dateKey), 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Box>

            {groupedMessages[dateKey].map((message) => {
              const isCurrentUser = message.sender_user_id === currentUserId;
              const time = format(new Date(message.sent_at), 'h:mm a');

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                    mb: 1,
                    maxWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: { xs: '85%', sm: '75%', md: '70%' },
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        backgroundColor: isCurrentUser ? 'primary.light' : 'background.paper',
                        borderRadius: 4,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        wordWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: isCurrentUser ? 'primary.contrastText' : 'text.primary',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {message.message}
                      </Typography>
                    </Paper>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {time}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </InfiniteScroll>
    </Box>
  );
};

export default MessageList;
